/**
 * Route definition for /profile/$userId
 *
 * Uses TanStack Router's loader to prefetch profile data server-side.
 * The profile data is fetched before the component renders, providing
 * instant page loads and built-in loading states.
 *
 * @module routes/profile/$userId
 * @category Routes
 */

import { createFileRoute, redirect } from '@tanstack/react-router'
import { toast } from 'react-toastify'
import { getUserProfile, ApiError } from '@/lib/api'
import ProfileContainer from '@/containers/Profile'
import { createProfileSchema } from '@/utils/metadata'
import { setPageMetadata } from '@/utils/page-metadata'

/**
 * Loader function that runs before the component renders.
 *
 * Server-side: fetches profile data and returns it
 * Client-side: provides cached data if available, refetches if stale
 *
 * @param {Object} params - Route parameters
 * @param {string} params.userId - The user ID from the URL
 * @returns {Promise<{profile: import('../../lib/api').User, userId: string}>}
 */
export const Route = createFileRoute('/$locale/profile/$userId')({
  /**
   * Preloads the profile data before component mount.
   * Supports SSR - data is fetched on server during initial render
   * and hydrated on client for seamless transition.
   *
   * Handles profile privacy: if profile is private and user is not authorized,
   * returns isPrivate flag instead of throwing error.
   */
  loader: async ({ params: { userId } }) => {
    // Validate userId exists
    if (!userId) {
      throw new Error('User ID is required')
    }

    try {
      // Fetch profile data using our typed API
      const profile = await getUserProfile(userId)

      // Return data to be available via Route.useLoaderData()
      return {
        profile,
        userId,
        isPrivate: false,
      }
    } catch (error) {
      // Handle private profile (403 Forbidden)
      if (error instanceof ApiError && error.status === 403) {
        console.log('Profile is private:', userId)
        return {
          profile: null,
          userId,
          isPrivate: true,
        }
      }

      // For other errors, log and show toast, then propagate to error boundary
      console.error('Failed to load profile:', error)
      toast.error('Failed to load profile. Please try again.')
      throw error
    }
  },

  /**
   * The component that renders the profile page.
   * Receives loader data via Route.useLoaderData() hook.
   */
  component: ProfilePage,
})

/**
 * Profile page component.
 *
 * Receives preloaded profile data from the loader and passes it to
 * the ProfileContainer. This eliminates the need for useEffect-based
 * data fetching and provides instant rendering.
 *
 * @returns {React.ReactElement}
 */
function ProfilePage() {
  // Get the preloaded data from the route loader
  const { profile, userId, isPrivate } = Route.useLoaderData()

  // Build full URL for the profile
  const pageUrl = `https://stealthisnft.xyz/profile/${userId}`

  // Profile image (use avatar or fallback)
  const profileImage = profile?.avatar || profile?.image || ''

  // If profile is private, render (metadata can be set by routeMetaMap fallback)
  if (isPrivate) {
    return (
      <ProfileContainer initialProfile={profile} userId={userId} isPrivate={isPrivate} />
    )
  }

  // Profile name and bio
  const profileName = profile?.username || profile?.display_name || userId
  const profileBio = profile?.bio || profile?.description || `View profile of ${userId} on VIRI NFT Marketplace`

  // Generate profile structured data
  const structuredData = createProfileSchema({
    name: profileName,
    bio: profileBio,
    url: pageUrl,
    image: profileImage,
    address: profile?.address || userId,
    ownedCount: profile?.owned_count || profile?.nfts?.length || 0,
    createdCount: profile?.created_count || 0,
  })

  // Set metadata including JSON-LD
  setPageMetadata({
    title: `${profileName} | VIRI`,
    description: profileBio,
    ogImage: profileImage || undefined,
    ogType: 'profile',
    twitterCard: 'summary_large_image',
    jsonLd: structuredData,
  })

  return (
    <ProfileContainer initialProfile={profile} userId={userId} isPrivate={isPrivate} />
  )
}
